import { API_URL } from '../constants';
import { IUserRestResponse, UserRest } from '../rest/user/userRest';
import { AnonymUserStorage } from './local-storage/user-state';

export class AuthManager {

  private static LOGIN_REDIRECT_REFERER = 'langmagic.com'
  private static AUTH_SERVICE_LOGIN_URL = API_URL + '/user/authServiceLogin';
  private static AUTH_SERVICE_SWITCH_ACCOUNT_URL = AuthManager.AUTH_SERVICE_LOGIN_URL + '?switchAccount=1';
  private static AUTH_SERVICE_CONFIG_URL = API_URL + '/user/authServiceConfig';
  // Per-browser: the user logged out and the auth service didn't confirm its own session ended, so
  // no tab may silently sign them back in until they ask (see logout / autoLogin).
  // Key also read/cleared by backend/html/landing.html — rename both together.
  private static SSO_SUPPRESSED_KEY = 'lm-sso-suppressed';
  // Where to come back to after the login round trip (see rememberReturnUrl). Also cleared by landing.html.
  private static SSO_RETURN_URL_KEY = 'lm-sso-return-url';
  // Per tab: the email an admin is logged in as, so a reload keeps it (a new tab starts as the admin).
  private static LOGIN_AS_EMAIL_KEY = 'lm-login-as-email';

  // Cached across calls so the backend is asked for AUTH_SERVICE_URL only once per page load,
  // not on every fetchAuthServiceUserCode()/callAuthServiceLogout() call.
  private static authServiceUrlPromise: Promise<string | null> | null = null;

  // GET .../authServiceConfig — this backend's own AUTH_SERVICE_URL env var, so the value only
  // has to be set in one place (the backend) instead of also baked into the frontend bundle at
  // build time.
  //
  // Three outcomes, and they must stay distinguishable: a URL, `''` for "this deploy has no usable
  // auth service" (the backend answers empty unless the whole login is configured), and `null` for
  // "couldn't ask". Callers that end a session may only act on the definite answers — the same
  // rule fetchAuthServiceUserCode follows.
  private static getAuthServiceUrl(): Promise<string | null> {
    if (!AuthManager.authServiceUrlPromise) {
      AuthManager.authServiceUrlPromise = fetch(AuthManager.AUTH_SERVICE_CONFIG_URL, { method: 'GET' })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('authServiceConfig ' + response.status)))
        .then((data) => typeof data?.authServiceUrl === 'string' ? data.authServiceUrl : '')
        // A failure must NOT be cached: one blip on bootstrap would otherwise leave this page load
        // permanently believing the auth service isn't configured. Drop the cached promise so the
        // next caller retries.
        .catch((e) => {
          console.log(e);
          AuthManager.authServiceUrlPromise = null;
          return null;
        });
    }
    return AuthManager.authServiceUrlPromise;
  }

  // Reconciles this app's own session with the shared auth service's, in that order: read the
  // session this backend already has, then ask the service once (via a temporary code, not a
  // redirect) whether that session is still current, still the same account, or worth starting
  // fresh from.
  // Second copy for guests on `/` (no bundle there): backend/html/landing.html — keep the rules in sync.
  public static async autoLogin(): Promise<IUserRestResponse | null> {
    const loginAs = await AuthManager.restoreLoginAs();
    if (loginAs) return loginAs;

    const local = await UserRest.getUserBySession();
    const code = await AuthManager.fetchAuthServiceUserCode();

    if (!code) {
      // No proof either way — see fetchAuthServiceUserCode. Stays unactionable: keep whatever
      // local session already exists (or stay anonymous), never clear or create one from this.
      return local?.authUser ? AuthManager.storeTokens(local) : null;
    }

    // Suppressed by an explicit logout whose remote leg went unconfirmed (see logout()): the auth
    // service may still hold a live session there, and silently signing back into it now is
    // exactly what the user just said they didn't want. Lifted the moment they ask for a login
    // themselves (beginLogin) or a session already stands (storeTokens).
    if (localStorage.getItem(AuthManager.SSO_SUPPRESSED_KEY)) {
      return local?.authUser ? AuthManager.storeTokens(local) : null;
    }

    // Exchanges the code for the profile server-side (AUTH_SERVICE_URL/user, POST {code} — the
    // same call the ordinary login redirect's /auth/callback makes) and mints/replaces this app's
    // own session for whoever that proves is signed in. Unlike the old /status hint, a `code` is
    // independently verified by the backend, so it's safe to act on directly — whether that means
    // starting a session with nothing here yet, or switching one already here to a different
    // account the auth service now names. A failed exchange (bad/expired code, auth service down)
    // falls back to the local session rather than losing it over a transient hiccup.
    const synced = await UserRest.syncAuthServiceSession(code);
    if (synced?.authUser) {
      return AuthManager.storeTokens(synced);
    }
    return local?.authUser ? AuthManager.storeTokens(local) : null;
  }

  // The round trip always lands back on `/?auth=1` (authCallback.ts redirects there, and it has no
  // way of knowing where the browser started), which is the call lobby — so without this a guest
  // opening /call/<roomId> would come back to the lobby with the room id gone. Stashed per tab and
  // restored by consumeAuthCallbackParams before <App/> mounts.
  //
  // Nothing is stashed for the lobby, `/`: coming back there already ends up at the lobby, so a
  // stash would only buy a second full page load for the same destination.
  public static rememberReturnUrl(): void {
    if (window.location.pathname === '/') {
      // Drop a stash a previous attempt may have left behind, rather than returning to a page this
      // one deliberately didn't ask for.
      sessionStorage.removeItem(AuthManager.SSO_RETURN_URL_KEY);
      return;
    }
    const url = window.location.pathname + window.location.search + window.location.hash;
    sessionStorage.setItem(AuthManager.SSO_RETURN_URL_KEY, url);
  }

  // Same-origin paths only: '//host' is a protocol-relative URL and would navigate off-site, so a
  // stored value that isn't a plain absolute path is dropped rather than followed.
  public static consumeReturnUrl(): string {
    const url = sessionStorage.getItem(AuthManager.SSO_RETURN_URL_KEY);
    sessionStorage.removeItem(AuthManager.SSO_RETURN_URL_KEY);
    if (!url || url.charAt(0) !== '/' || url.charAt(1) === '/') {
      return '';
    }
    return url;
  }

  // Reached whenever a session (local or freshly synced) is accepted as current — there is
  // nothing left to suppress once the user is signed in here.
  private static storeTokens(result: IUserRestResponse): IUserRestResponse {
    localStorage.removeItem(AuthManager.SSO_SUPPRESSED_KEY);
    TokenStorage.setAccessToken(result.authUser.token);
    TokenStorage.setRefreshToken(result.authUser.refreshToken);
    return result;
  }

  // Direct browser fetch to AUTH_SERVICE_URL/user-code (not proxied through this app's backend),
  // so the request carries that service's own session cookie — which this backend never sees, and
  // which the browser may well refuse to send here at all (see autoLogin). Every unhappy path
  // therefore collapses to the same thing, an absent code: a hint that can open a session sync,
  // and nothing this app does may depend on its absence meaning "signed out".
  private static async fetchAuthServiceUserCode(): Promise<string> {
    const authServiceUrl = await AuthManager.getAuthServiceUrl();
    if (!authServiceUrl) {
      return '';
    }
    try {
      const response = await fetch(`${authServiceUrl}/user-code`, { method: 'GET', credentials: 'include' });
      if (!response.ok) {
        return '';
      }
      const data = await response.json();
      return typeof data?.code === 'string' ? data.code : '';
    } catch (e) {
      console.log(e);
      return '';
    }
  }

  /*private static isNeedRedirectToLogin() {
    return !!document?.referrer && document.referrer.includes(this.LOGIN_REDIRECT_REFERER)
  }*/

  // Full top-level redirect to the shared auth service (same one youtube-grazer/the-endless-book
  // use) instead of a Google popup — the browser navigates away entirely, so this never resolves
  // in this page's lifetime. The lm_sess_user cookie the callback sets is picked up on the next
  // load by the existing autoLogin() bootstrap (see MainContainerEvents.onLoad and friends).
  public static async login(): Promise<IUserRestResponse | null> {
    return AuthManager.beginLogin(false);
  }

  // landing.html's sign-in link repeats this storage cleanup — keep the two in sync.
  private static async beginLogin(switchAccount: boolean): Promise<IUserRestResponse | null> {
    // Asking to sign in lifts the suppression a previous logout may have left behind — it only
    // ever meant "don't sign me back in on my own".
    localStorage.removeItem(AuthManager.SSO_SUPPRESSED_KEY);
    // Same round trip, same lost URL (see rememberReturnUrl) — signing in from a room link should
    // land back in that room, not in the lobby.
    AuthManager.rememberReturnUrl();
    window.location.href = switchAccount
      ? AuthManager.AUTH_SERVICE_SWITCH_ACCOUNT_URL
      : AuthManager.AUTH_SERVICE_LOGIN_URL;
    return new Promise(() => { });
  }

  // The auth service silently returns the current session, so switching first has to end it.
  public static async switchAccount(): Promise<IUserRestResponse | null> {
    try {
      await AuthManager.logout();
    } catch (e) {
      // logout() already made the best-effort remote sign-out in its finally block.
      console.log(e);
    }
    return AuthManager.beginLogin(true);
  }

  public static async loginByEmail(email: string): Promise<IUserRestResponse | null> {
    const result = await UserRest.getUserByEmail(email);
    if (result && result.authUser) {
      TokenStorage.setAccessToken(result.authUser.token);
      TokenStorage.setRefreshToken(result.authUser.refreshToken);
      sessionStorage.setItem(AuthManager.LOGIN_AS_EMAIL_KEY, email);
    }
    return result;
  }

  // Re-enters an admin's "Login as" after a reload; drops it if the admin session no longer allows it.
  private static async restoreLoginAs(): Promise<IUserRestResponse | null> {
    const email = sessionStorage.getItem(AuthManager.LOGIN_AS_EMAIL_KEY);
    if (!email) return null;
    const result = await UserRest.getUserByEmailWithoutToken(email);
    if (result?.authUser) return AuthManager.storeTokens(result);
    sessionStorage.removeItem(AuthManager.LOGIN_AS_EMAIL_KEY);
    return null;
  }

  public static updateUserToken(token: string) {
    TokenStorage.setAccessToken(token);
  }

  public static async loadAnonymous(targetLangCode: string, nativeLangCode: string): Promise<IUserRestResponse | null> {
    let result = await this.getAnonymous();
    if (result?.authUser) {
      return result;
    }

    return this.createAnonymous(targetLangCode, nativeLangCode);
  }

  // Never throws: every caller treats a missing authUser (no session to proceed with) the same
  // way whether the backend explicitly said no or the request itself failed, so a network/5xx
  // failure here resolves to null instead of becoming an unhandled rejection.
  public static async createAnonymous(targetLangCode: string, nativeLangCode: string): Promise<IUserRestResponse | null> {
    try {
      const result: IUserRestResponse = await UserRest.createAnonymousUserAction(targetLangCode, nativeLangCode);
      if (result?.authUser) {
        AnonymUserStorage.setHash(result?.authUser.hashId);
      }
      return result;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  public static async getAnonymous(): Promise<IUserRestResponse | null> {
    const userHash = AnonymUserStorage.getHash();
    if (!!userHash) {
      const result = await UserRest.getUserByHash(userHash);
      if (result?.authUser) {
        return result;
      }
    }
    return null;
  }

  public static async getUserDataByAuthCode(code: string, callback: boolean = false): Promise<IUserRestResponse | null> {
    const result = await UserRest.getAuthUserByCode(code, callback);
    if (result && result.authUser) {
      TokenStorage.setAccessToken(result.authUser.token);
      TokenStorage.setRefreshToken(result.authUser.refreshToken);
    }
    return result;
  }

  // The auth service's own logout has to run even if this app's logout fails: leave that session
  // alive and the next bootstrap signs the user straight back in, so a failed local logout would
  // silently undo itself.
  public static async logout() {
    sessionStorage.removeItem(AuthManager.LOGIN_AS_EMAIL_KEY);
    TokenStorage.remove();
    AnonymUserStorage.remove();
    try {
      await UserRest.logout();
    } finally {
      // All of this belongs in the `finally`: UserRest.logout() goes through withAuthRetry, which
      // rejects on a network error or a 5xx, and that is exactly the case worth guarding against —
      // the backend may well have cleared its cookies before the response was lost, leaving the
      // remote session the only thing still standing.
      const remoteSessionEnded = await AuthManager.callAuthServiceLogout();
      // An explicit logout must not be undone by the silent sign-in on the next load. Unless the
      // auth service confirmed its own session is gone, assume it is still there and suppress the
      // silent sync until the user asks for one. This lives in localStorage, not per-tab: logging
      // out is a decision about this browser, and another tab (or the next one) would otherwise
      // walk straight back in through /user-code.
      if (remoteSessionEnded) {
        localStorage.removeItem(AuthManager.SSO_SUPPRESSED_KEY);
      } else {
        localStorage.setItem(AuthManager.SSO_SUPPRESSED_KEY, '1');
      }
    }
  }

  // Ends the session on the shared auth service too — a direct browser fetch (not proxied through
  // this app's backend) so AUTH_SERVICE_URL's own session cookie goes with it. Returns whether that
  // session can be considered gone: a network failure or an error status means it may well still be
  // there, and the caller has to act as if it is.
  private static async callAuthServiceLogout(): Promise<boolean> {
    const authServiceUrl = await AuthManager.getAuthServiceUrl();
    if (authServiceUrl === null) {
      // Couldn't even learn where to send the logout, so it certainly wasn't delivered: the remote
      // session may well be alive, and this has to count as unconfirmed. (Distinct from `''`
      // below — conflating the two is how a failed config fetch would let the next bootstrap sign
      // the user back in.)
      return false;
    }
    if (!authServiceUrl) {
      // Definitively not configured: no auth-service session is in play here, so there is nothing
      // that could sign the user back in.
      return true;
    }
    try {
      const response = await fetch(`${authServiceUrl}/logout`, { method: 'GET', credentials: 'include' });
      return response.ok;
    } catch (e) {
      console.log(e);
      return false;
    }
  }

}

class TokenStorage {

  private static ACCESS_KEY_NAME = 'user-access-token';
  private static REFRESH_KEY_NAME = 'user-refresh-token';

  public static setAccessToken(value: string) {
    sessionStorage.setItem(TokenStorage.ACCESS_KEY_NAME, value);
    localStorage.setItem(TokenStorage.ACCESS_KEY_NAME, value);
  }

  public static setRefreshToken(value: string) {
    sessionStorage.setItem(TokenStorage.REFRESH_KEY_NAME, value);
    localStorage.setItem(TokenStorage.REFRESH_KEY_NAME, value);
  }

  public static remove() {
    sessionStorage.removeItem(TokenStorage.ACCESS_KEY_NAME);
    localStorage.removeItem(TokenStorage.ACCESS_KEY_NAME);
    sessionStorage.removeItem(TokenStorage.REFRESH_KEY_NAME);
    localStorage.removeItem(TokenStorage.REFRESH_KEY_NAME);
  }
}
